import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Tag, Image, Dropdown, Flex, Spin, Avatar } from "antd"
import { UserOutlined } from "@ant-design/icons"
import DSTab from "ui/DSTab"
import { Icon } from "ui/Icon"
import { routes } from "router/routes"
import { getCrewProfile, updateCrewProfile, getAssignedExperiences } from "store/crew-profiles"
import { getRoles } from "store/roles"
import UpcomingBookings from "./components/UpcomingBookings"
import Availability from "./components/Availability"
import ScheduledTimeOff from "./components/ScheduledTimeOff"
import AssignedExperiences from "./components/AssignedExperiences"
import LicensesCertifications from "./components/LicensesCertifications"
import Calendar from "./components/Calendar"
import EditCrewMemberModal from "./components/EditCrewMemberModal"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import DeactivateDeleteModals from "./components/DeactivateDeleteModals"
import BookingConflictModal from "components/common/BookingConflictModal"
import RemoveExperienceWithBookingsModal from "ui/RemoveExperienceWithBookingsModal"
import { useCrewDeactivateModal } from "./hooks/useCrewDeactivateModal"
import { useBookingConflict } from "hooks/useBookingConflict"
import { useProfileDetailsModals } from "hooks/useProfileDetailsModals"
import { useDevice } from "hooks/useDevice"
import styles from "./index.module.css"
import MoreActionEllipsis from "ui/MoreActionEllipsis"
import { capitalizeFirstLetter } from "utils/data-formatters"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

// Transform API data to component format
const transformProfileData = (apiData) => {
  if (!apiData) return null

  return {
    id: apiData.id,
    firstName: apiData.first_name,
    lastName: apiData.last_name,
    name: `${apiData.first_name} ${apiData.last_name}`,
    email: apiData.email,
    phoneNumber: apiData.phone_number,
    phoneCountryCode: apiData.country_code,
    title: apiData.title,
    role: apiData.role?.name,
    roleId: apiData.role?.id,
    description: apiData.description,
    status: apiData.status,
    image: apiData.photo_url || apiData.photo,
    can_edit: apiData.can_edit,
    is_password_set: apiData.is_password_set || false,
    upcomingBookings: apiData.upcoming_bookings || [],
    availability: apiData.availability || {},
    scheduledTimeOff: apiData.scheduled_time_off || [],
    assignedExperiences: apiData.assigned_experiences || [],
    licenses: apiData.licenses || []
  }
}

export default function CrewMemberDetailPage({ context = "crew" }) {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { hasPermission } = usePermissions()
  const canViewCalendar = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW)
  const [isAssignExperiencesModalOpen, setIsAssignExperiencesModalOpen] = useState(false)

  const isEditModalOpen = location.pathname.endsWith("/edit")

  // Deactivate/Delete/Activate modal hook
  const {
    deactivateDeleteModalState,
    activateModalVisible,
    selectedCrew,
    openDeactivateModal,
    openDeleteModal,
    openActivateModal,
    closeDeactivateDeleteModal,
    handleConfirmDeactivateDelete,
    handleActivateConfirm,
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  } = useCrewDeactivateModal()

  // Booking conflict hook for assigning experiences
  const { handleBookingConflictError, bookingConflictModalProps } = useBookingConflict({
    titleKey: "crewMember.assignExperiences.bookingConflictList.title",
    messageKey: "crewMember.assignExperiences.bookingConflictList.message",
    closeButtonKey: "crewMember.scheduledTimeOff.buttons.close"
  })

  // Redux state
  const { profile } = useSelector((state) => state.crewProfiles)
  const { data: assignedExperiencesData = [] } = useSelector((state) => state.crewProfiles.assignedExperiences)

  // Fetch crew profile and roles on mount
  useEffect(() => {
    dispatch(getCrewProfile(id))
    dispatch(getRoles())
  }, [dispatch, id])

  // Transform API data for display
  const crewMember = transformProfileData(profile.data)
  const { image, name, role, status, title, description, can_edit, is_password_set } = crewMember || {}
  const isLoading = profile.loading

  // Profile details modals hook for conflict handling
  const { handleProfileUpdate, assignConflictModalProps } = useProfileDetailsModals(id, name, updateCrewProfile, getAssignedExperiences)

  // Determine active tab from URL
  const activeTab = location.pathname.includes("/calendar") ? "calendar" : "details"

  // Redirect to details if on base crew member URL or if trying to access calendar without permission
  useEffect(() => {
    if (location.pathname === `/pro/home/resources/crew/${id}`) {
      navigate(routes.guideCrewMemberDetailsPath({ id }), { replace: true })
    } else if (location.pathname.includes("/calendar") && !canViewCalendar) {
      navigate(routes.guideCrewMemberDetailsPath({ id }), { replace: true })
    }
  }, [location.pathname, id, navigate, canViewCalendar])

  const editPath = context === "crew" ? routes.guideCrewMemberEditPath({ id }) : routes.proStaffEditPath({ id })

  const handleEdit = () => {
    navigate(editPath)
  }

  const handleEditCancel = () => {
    navigate(-1)
  }

  /**
   * Builds trip_assignments_attributes array for saving experience assignments
   * @param {Array<number>} selectedExperienceIds - Complete list of trip IDs that should be assigned
   * @param {Array<Object>} currentAssignments - Current assignment data with trip_assignment_id and trip_id
   * @returns {Array<Object>} Array of trip assignment objects with proper structure
   */
  const buildTripAssignmentsAttributes = (selectedExperienceIds, currentAssignments) => {
    const tripAssignmentsAttributes = []

    // Normalize selected IDs to numbers for comparison
    const selectedTripIds = selectedExperienceIds.map(Number)

    // Create a map of current assignments for easy lookup (trip_id -> assignment_id)
    const currentAssignmentsMap = new Map(
      currentAssignments.map((assignment) => [Number(assignment.trip_id), assignment.trip_assignment_id])
    )

    // Process all current assignments (either keep or delete)
    currentAssignments.forEach((assignment) => {
      const tripId = Number(assignment.trip_id)
      if (selectedTripIds.includes(tripId)) {
        // Keep: assignment exists in both current and selected
        tripAssignmentsAttributes.push({
          id: assignment.trip_assignment_id,
          trip_id: assignment.trip_id
        })
      } else {
        // Delete: assignment exists in current but not in selected
        tripAssignmentsAttributes.push({
          id: assignment.trip_assignment_id,
          trip_id: assignment.trip_id,
          _destroy: true
        })
      }
    })

    // Handle additions: Find trip IDs that are in selected but not in current
    selectedTripIds.forEach((tripId) => {
      if (!currentAssignmentsMap.has(tripId)) {
        tripAssignmentsAttributes.push({
          trip_id: tripId
        })
      }
    })

    return tripAssignmentsAttributes
  }
  const handleBack = () => {
    navigate(-1)
  }

  const handleActivate = () => {
    openActivateModal({ id, name, role })
  }

  const handleTabChange = (key) => {
    if (key === "details") {
      navigate(routes.guideCrewMemberDetailsPath({ id }))
    } else if (key === "calendar") {
      navigate(routes.guideCrewMemberCalendarPath({ id }))
    }
  }

  const tabItems = [
    {
      key: "details",
      label: t("crewMember.detail.tabs.details")
    },
    ...(canViewCalendar
      ? [
          {
            key: "calendar",
            label: t("crewMember.detail.tabs.calendar")
          }
        ]
      : [])
  ]

  // Show loading state
  if (isLoading && !crewMember) {
    return (
      <Flex vertical gap={16} className={`${styles.container} ${styles.loadingContainer}`} align="center" justify="center">
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16} className={styles.container}>
      {/* Hide crew detail content when edit modal is open on mobile */}
      {!(isEditModalOpen && isMobile) && (
        <>
          {/* Back Button */}
          <Flex align="center" className={`mt-m ${styles.pageNameSection}`}>
            <Flex align="center" justify="center" onClick={handleBack} className={styles.backButton}>
              <Icon width={16} height={16} type="feather" name="ChevronLeft" color="currentColor" />
            </Flex>
          </Flex>
          {/* Header Section */}
          <div className={styles.headerCard}>
            <Flex align="center" gap={16} className={styles.profileSection}>
              {image ? (
                <Image src={image} width={80} height={80} className={styles.profileImage} preview={false} />
              ) : (
                <Avatar icon={<UserOutlined />} size={80} />
              )}

              <Flex vertical gap={4} className={styles.profileInfo}>
                <Flex align="center" gap={24} className={styles.nameRow}>
                  <h1 className={styles.name}>{name}</h1>
                  <Flex gap={8} className={styles.badges}>
                    <Tag className={styles.roleTag}>{role}</Tag>
                    <Tag color={status === "active" ? "green" : "red"} className={styles.statusTag}>
                      {capitalizeFirstLetter(status)}
                    </Tag>
                  </Flex>
                </Flex>
                <p className={styles.title}>{title}</p>
                <p className={styles.description} title={description}>
                  {description}
                </p>
              </Flex>
              {can_edit && (
                <Flex gap={12} align="center" className={styles.actions}>
                  <Button type="primary" onClick={handleEdit} className={styles.editButton}>
                    {t("crewMember.detail.buttons.edit")}
                  </Button>
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: status?.toLowerCase() === "deactivated" ? "activate" : "deactivate",
                          label: status?.toLowerCase() === "deactivated" ? "Make Active" : "Deactivate",
                          disabled: status?.toLowerCase() === "deactivated" && !is_password_set
                        },
                        { key: "delete", label: t("crewMember.detail.actions.delete"), danger: true }
                      ],
                      onClick: ({ key }) => {
                        if (key === "deactivate") {
                          openDeactivateModal({ id, name, role })
                        } else if (key === "activate") {
                          handleActivate()
                        } else if (key === "delete") {
                          openDeleteModal({ id, name, role })
                        }
                      }
                    }}
                    placement="bottomRight"
                  >
                    <MoreActionEllipsis ellipsisDirection="vertical" />
                  </Dropdown>
                </Flex>
              )}
            </Flex>
          </div>

          {/* Tabs */}
          <div className={styles.tabsContainer}>
            <DSTab items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
          </div>

          {/* Content */}
          {activeTab === "details" && (
            <Flex vertical gap={16} className={styles.content}>
              {/* Upcoming Bookings Section */}
              <UpcomingBookings crewMemberId={id} />

              {/* Availability and Time Off Row */}
              <div className={styles.twoColumnRow}>
                <Availability crewMemberId={id} can_edit={can_edit} />
                <ScheduledTimeOff crewMemberId={id} can_edit={can_edit} />
              </div>

              {/* Assigned Experiences and Licenses Row */}
              <div className={styles.twoColumnRow}>
                <AssignedExperiences
                  crewMemberId={id}
                  onAssign={() => setIsAssignExperiencesModalOpen(true)}
                  crewMemberName={name}
                  can_edit={can_edit}
                />
                <LicensesCertifications crewMemberId={id} can_edit={can_edit} />
              </div>
            </Flex>
          )}

          {activeTab === "calendar" && canViewCalendar && (
            <Flex vertical gap={16} className={styles.content}>
              <Calendar crewMemberId={id} />
            </Flex>
          )}
        </>
      )}

      {/* Edit Crew Member Modal */}
      <EditCrewMemberModal
        open={isEditModalOpen}
        onCancel={handleEditCancel}
        onSave={() => {
          dispatch(getCrewProfile(id))
          navigate(-1)
        }}
        crewMember={{ id }}
        context={context}
      />

      {/* Assign Experiences Modal */}
      <AssignExperiencesModal
        open={isAssignExperiencesModalOpen}
        onCancel={() => setIsAssignExperiencesModalOpen(false)}
        onSave={async (selectedExperienceIds) => {
          try {
            const tripAssignmentsAttributes = buildTripAssignmentsAttributes(selectedExperienceIds, assignedExperiencesData)

            // Create JSON payload with array of objects
            const payload = {
              trip_assignments_attributes: tripAssignmentsAttributes
            }

            await handleProfileUpdate(id, payload)
            setIsAssignExperiencesModalOpen(false)
          } catch (error) {
            // If booking conflict, the hook already opened the conflict modal
            if (error?.isBookingConflict) {
              setIsAssignExperiencesModalOpen(false)
              throw error // Re-throw to prevent success toast in AssignExperiencesModal
            }
            // For other errors, use the existing conflict handler
            handleBookingConflictError(error)
            throw error // Re-throw to prevent success toast
          }
        }}
        currentExperiences={assignedExperiencesData.map((exp) => ({ id: exp.trip_id, title: exp.trip_title }))}
        emptyText={t("crewMember.no_experience_assigned", { defaultValue: "No experience assigned to this crew member." })}
      />

      {/* Deactivate/Delete/Activate Modals */}
      <DeactivateDeleteModals
        modalState={deactivateDeleteModalState}
        activateModalVisible={activateModalVisible}
        selectedCrew={selectedCrew}
        onClose={closeDeactivateDeleteModal}
        onConfirm={async (data) => {
          const result = await handleConfirmDeactivateDelete(data)
          if (result.success) {
            if (result.action === "delete") {
              navigate(routes.guideCrewMembersPath())
            } else {
              dispatch(getCrewProfile(id))
            }
          }
        }}
        onActivateConfirm={async () => {
          const result = await handleActivateConfirm()
          if (result?.success) {
            dispatch(getCrewProfile(id))
          }
        }}
        deactivateLoading={deactivateLoading}
        deleteLoading={deleteLoading}
        deactivationDetailsLoading={deactivationDetailsLoading}
        deletionDetailsLoading={deletionDetailsLoading}
        t={t}
        context={context}
      />

      {/* Booking Conflict Modal */}
      <BookingConflictModal {...bookingConflictModalProps} />

      {/* Assign Experiences Conflict Modal */}
      <RemoveExperienceWithBookingsModal {...assignConflictModalProps} resourceType="crew" />
    </Flex>
  )
}

CrewMemberDetailPage.propTypes = {
  context: PropTypes.oneOf(["crew", "staff"])
}
